import { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from '../i18n/LanguageContext.jsx';
import { TransformWrapper, TransformComponent, useControls } from 'react-zoom-pan-pinch';
import './TimelineImage.css';

/**
 * ZoomControls — renders zoom buttons inside TransformWrapper context.
 * Must be a child of TransformWrapper to access useControls().
 */
function ZoomControls() {
  const { t } = useTranslation();
  const { zoomIn, zoomOut, resetTransform } = useControls();
  return (
    <div
      className="timeline-image__zoom-controls"
      role="toolbar"
      aria-label={t('interview.zoomControlsLabel')}
    >
      <button onClick={() => zoomIn()} type="button" aria-label={t('interview.zoomIn')}>
        +
      </button>
      <button onClick={() => zoomOut()} type="button" aria-label={t('interview.zoomOut')}>
        −
      </button>
      <button onClick={() => resetTransform()} type="button" aria-label={t('interview.resetZoom')}>
        ⟲
      </button>
    </div>
  );
}

/**
 * TimelineImage — displays Peter's professional timeline
 * as a responsive, centered image with descriptive alt text.
 * Clicking the image opens a fullscreen lightbox with pan/zoom via react-zoom-pan-pinch.
 */
function TimelineImage() {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef(null);
  const closeRef = useRef(null);
  const overlayRef = useRef(null);

  const openLightbox = useCallback(() => setIsOpen(true), []);
  const closeLightbox = useCallback(() => {
    setIsOpen(false);
    triggerRef.current?.focus();
  }, []);

  /* Escape key handler, focus trap & body scroll lock */
  useEffect(() => {
    if (!isOpen) return;

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'Tab') {
        /* Cycle focus through all interactive elements in the lightbox */
        const overlay = overlayRef.current;
        if (!overlay) return;
        const focusable = Array.from(overlay.querySelectorAll('button'));
        if (focusable.length === 0) return;
        const currentIndex = focusable.indexOf(document.activeElement);
        e.preventDefault();
        if (e.shiftKey) {
          const prevIndex = currentIndex <= 0 ? focusable.length - 1 : currentIndex - 1;
          focusable[prevIndex].focus();
        } else {
          const nextIndex = currentIndex >= focusable.length - 1 ? 0 : currentIndex + 1;
          focusable[nextIndex].focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen, closeLightbox]);

  /* Close only when clicking the backdrop itself */
  const handleBackdropClick = useCallback(
    (e) => {
      if (e.target === overlayRef.current) closeLightbox();
    },
    [closeLightbox]
  );

  return (
    <div className="timeline-image">
      <button
        ref={triggerRef}
        className="timeline-image__trigger"
        onClick={openLightbox}
        type="button"
        aria-haspopup="dialog"
      >
        <img
          src="/images/peter-timeline.png"
          alt={t('interview.timelineAlt')}
          className="timeline-image__img"
          loading="lazy"
        />
        <span className="timeline-image__hint">{t('interview.clickToEnlarge')}</span>
      </button>

      {isOpen && (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions -- keyboard handled via document listener
        <div
          ref={overlayRef}
          className="timeline-image__overlay"
          role="dialog"
          aria-modal="true"
          aria-label={t('interview.lightboxLabel')}
          onClick={handleBackdropClick}
        >
          <button
            ref={closeRef}
            className="timeline-image__close"
            onClick={closeLightbox}
            type="button"
            aria-label={t('interview.lightboxClose')}
          >
            ✕
          </button>
          <TransformWrapper
            initialScale={1}
            minScale={0.5}
            maxScale={5}
            centerOnInit={true}
            wheel={{ step: 0.1 }}
            panning={{ velocityDisabled: false }}
            doubleClick={{ mode: 'toggle' }}
            limitToBounds={false}
          >
            <ZoomControls />
            <TransformComponent
              wrapperClass="timeline-image__transform-wrapper"
              contentClass="timeline-image__transform-content"
            >
              <img
                src="/images/peter-timeline.png"
                alt={t('interview.timelineAlt')}
                className="timeline-image__overlay-img"
                draggable={false}
              />
            </TransformComponent>
          </TransformWrapper>
        </div>
      )}
    </div>
  );
}

export default TimelineImage;
